/* ============================================================
   Predictive search — Dawn-style debounced /search/suggest fetch.

   Wraps the header search input in a <predictive-search> custom
   element. As the user types we debounce, then hit:

     /search/suggest?q=<terms>
       &resources[limit]=6
       &resources[options][fields]=title,vendor,product_type,variants.title
       &section_id=predictive-search

   The response is the full <html> of sections/predictive-search.liquid.
   We extract #predictive-search-results and swap it into the drawer.

   Keyboard:
     ArrowDown / ArrowUp — move focus between result links
     Enter on input      — submit form (full search page)
     Escape              — clear + close drawer
   ============================================================ */

(() => {
  if (customElements.get('predictive-search')) return;

  const DEBOUNCE_MS = 250;
  const MIN_QUERY_LEN = 2;
  const ENDPOINT = '/search/suggest';
  const SECTION_ID = 'predictive-search';

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input        = null;
      this.panel        = null;
      this.results      = null;
      this.fallback     = null;
      this.loading      = null;
      this._timer       = null;
      this._abort       = null;
      this._cache       = new Map();    // q → html
      this._activeIndex = -1;
      this._items       = [];
    }

    connectedCallback() {
      this.input    = this.querySelector('[data-predictive-input]');
      this.panel    = this.querySelector('[data-predictive-panel]');
      this.results  = this.querySelector('[data-predictive-results]');
      this.fallback = this.querySelector('[data-predictive-fallback]');
      this.loading  = this.querySelector('[data-predictive-loading]');
      if (!this.input || !this.panel) return;

      this._onInput      = this._onInput.bind(this);
      this._onKeydown    = this._onKeydown.bind(this);
      this._onFocus      = this._onFocus.bind(this);
      this._onPanelClick = this._onPanelClick.bind(this);

      this.input.addEventListener('input',   this._onInput);
      this.input.addEventListener('keydown', this._onKeydown);
      this.input.addEventListener('focus',   this._onFocus);
      this.panel.addEventListener('click',   this._onPanelClick);
    }

    disconnectedCallback() {
      this.input?.removeEventListener('input',   this._onInput);
      this.input?.removeEventListener('keydown', this._onKeydown);
      this.input?.removeEventListener('focus',   this._onFocus);
      this.panel?.removeEventListener('click',   this._onPanelClick);
      if (this._abort) this._abort.abort();
      clearTimeout(this._timer);
    }

    /* ============ Event handlers ============ */

    _onInput() {
      const q = this.input.value.trim();
      clearTimeout(this._timer);
      if (q.length < MIN_QUERY_LEN) {
        this._showFallback();
        return;
      }
      this._timer = setTimeout(() => this._fetch(q), DEBOUNCE_MS);
    }

    _onFocus() {
      const q = this.input.value.trim();
      if (q.length >= MIN_QUERY_LEN && this.results.children.length) {
        this._showPanel();
      }
    }

    _onKeydown(event) {
      const key = event.key;
      if (key === 'Escape') {
        if (this.input.value) {
          this.input.value = '';
          this._showFallback();
          event.preventDefault();
        }
        return;
      }
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        this._collectItems();
        if (!this._items.length) return;
        event.preventDefault();
        const dir = key === 'ArrowDown' ? 1 : -1;
        this._activeIndex = (this._activeIndex + dir + this._items.length) % this._items.length;
        this._highlight();
      }
      if (key === 'Enter') {
        if (this._activeIndex >= 0 && this._items[this._activeIndex]) {
          event.preventDefault();
          this._items[this._activeIndex].click();
        }
      }
    }

    _onPanelClick(event) {
      // Let the link navigate. Just dispatch a close so the drawer slides up
      // once navigation kicks in (avoids stuck drawer state).
      const link = event.target.closest('[data-predictive-link]');
      if (!link) return;
      // Don't close on mod-click (new tab).
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
      const drawer = document.getElementById('searchDrawer');
      if (drawer) drawer.classList.remove('is-open');
      const backdrop = document.getElementById('searchBackdrop');
      if (backdrop) backdrop.classList.remove('is-active');
    }

    /* ============ Fetch + render ============ */

    async _fetch(q) {
      if (this._cache.has(q)) {
        this._renderHtml(this._cache.get(q));
        return;
      }

      if (this._abort) this._abort.abort();
      this._abort = new AbortController();

      const url = new URL(ENDPOINT, window.location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,vendor,product_type,variants.title,body');
      url.searchParams.set('section_id', SECTION_ID);

      this._showLoading();

      try {
        const res = await fetch(url.toString(), { signal: this._abort.signal });
        if (!res.ok) throw new Error(`Predictive search ${res.status}`);
        const html = await res.text();
        this._cache.set(q, html);
        if (this._cache.size > 30) this._cache.delete(this._cache.keys().next().value);
        this._renderHtml(html);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('[predictive-search] fetch failed', err);
          this._showFallback();
        }
      }
    }

    _renderHtml(html) {
      // The section endpoint returns the whole rendered section. Extract
      // just the #predictive-search-results inner HTML.
      const doc  = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('#predictive-search-results');
      if (!inner) { this._showFallback(); return; }
      this.results.innerHTML = inner.innerHTML;
      this._activeIndex = -1;
      this._collectItems();
      this._showPanel();
    }

    /* ============ State toggles ============ */

    _showPanel() {
      if (this.fallback) this.fallback.hidden = true;
      if (this.loading)  this.loading.hidden  = true;
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    _showLoading() {
      if (this.fallback) this.fallback.hidden = true;
      this.panel.hidden = false;
      if (this.loading) this.loading.hidden = false;
    }

    _showFallback() {
      if (this._abort) this._abort.abort();
      this.results.innerHTML = '';
      if (this.fallback) this.fallback.hidden = false;
      this.panel.hidden = true;
      if (this.loading) this.loading.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this._activeIndex = -1;
      this._items = [];
    }

    /* ============ Keyboard nav helpers ============ */

    _collectItems() {
      this._items = Array.from(this.results.querySelectorAll('[data-predictive-link]'));
    }

    _highlight() {
      this._items.forEach((el, i) => {
        if (i === this._activeIndex) {
          el.classList.add('is-active');
          el.scrollIntoView({ block: 'nearest' });
        } else {
          el.classList.remove('is-active');
        }
      });
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
